import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import type { WithdrawalListItemDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { InvoiceDownloadIconButton } from "Invoices/Components/InvoiceDownloadIconButton";
import { DetailActionCell } from "Shared/DataTable/DetailActionCell";
import { NameLinkCell } from "Shared/NameLinkCell";
import { Price } from "Shared/Price/Price";
import { DateFormat, formatDate } from "Shared/Utils/Format/date";
import { WithdrawalStatusChip } from "WithdrawalApprovals/Components/WithdrawalStatusChip";

const columnHelper = createColumnHelper<WithdrawalListItemDto>();

export const WITHDRAWAL_SORTABLE_COLUMNS = [
  "actorName",
  "amount",
  "status",
  "requestedAt",
] as const;

const isSortable = (id: string) =>
  (WITHDRAWAL_SORTABLE_COLUMNS as readonly string[]).includes(id);

export function useWithdrawalApprovalsTableColumns() {
  const { t } = useResource();

  return useMemo(
    () => [
      columnHelper.accessor("actorName", {
        header: t(Resources.WithdrawalApprovals.ActorName),
        enableSorting: isSortable("actorName"),
        cell: (info) => (
          <NameLinkCell
            path={AppRouting.WithdrawalApprovalDetail.path(
              info.row.original.withdrawalRequestID,
            )}
          >
            {info.getValue() ?? "-"}
          </NameLinkCell>
        ),
      }),
      columnHelper.accessor("amount", {
        header: t(Resources.WithdrawalApprovals.Amount),
        enableSorting: isSortable("amount"),
        cell: (info) => <Price amount={info.getValue()} />,
      }),
      columnHelper.accessor("status", {
        header: t(Resources.WithdrawalApprovals.Status),
        enableSorting: isSortable("status"),
        cell: (info) => <WithdrawalStatusChip status={info.getValue()} />,
      }),
      columnHelper.accessor("requestedAt", {
        header: t(Resources.WithdrawalApprovals.RequestedAt),
        enableSorting: isSortable("requestedAt"),
        cell: (info) => formatDate(info.getValue(), DateFormat.DateTime),
      }),
      columnHelper.display({
        id: "invoice",
        header: "",
        enableSorting: false,
        cell: (info) => {
          const invoiceID = info.row.original.invoiceID;
          if (!invoiceID) return null;
          return <InvoiceDownloadIconButton invoiceID={invoiceID} />;
        },
      }),
      columnHelper.display({
        id: "action",
        header: "",
        enableSorting: false,
        meta: { maxWidth: 50, justifySelf: "end" },
        cell: (info) => (
          <DetailActionCell
            path={AppRouting.WithdrawalApprovalDetail.path(
              info.row.original.withdrawalRequestID,
            )}
          />
        ),
      }),
    ],
    [t],
  );
}
